import React, { useEffect, useMemo, useState } from 'react'
import { CssBaseline } from '@mui/material'
import {
  ThemeProvider,
  createTheme,
  responsiveFontSizes,
  StyledEngineProvider,
} from '@mui/material/styles'
import { observer } from 'mobx-react-lite'
import { isMobile } from 'react-device-detect'
import { Router, Route, Switch, useHistory, useRouteMatch, useLocation } from 'react-router-dom'
import { load, RootStoreProvider, RootStoreType, setupRootStore, useStores, i1 } from '@trivie/core'
import { ScrollToTop } from '@trivie/ui-web/components/scroll-to-top'
import { MiddlewareDialog } from '@trivie/ui-web/components/middleware-dialog'
import { MaintenanceDialog } from '@trivie/ui-web/components/maintenance-dialog'
import { decode } from 'base-64'
import { useTranslation } from 'react-i18next'

import BrowserHistory from './history'
import { useStatusCodeMiddleware } from './middleware'
import { baseTheme, muiTheme } from '@trivie/ui-web/theme'
import { loginRoutes, RouteType } from './routing'
import { ProtectedRoutes } from './routing/protected-routes'
import { I18nextProvider } from 'react-i18next'

import * as Sentry from '@sentry/react'
import { SSOEntryScreen } from './screens/login/sso-entry'
import { Logout } from './screens/logout'
import { BrandedSplashScreen } from './screens/splash/branded-splash-screen'
import { SMSScreen } from './screens/login/sms/sms-screen'
import { ShareQuizScreen } from './screens/onboarding/share-quiz-screen'
import { TestLogin } from './screens/test-login'
import { FullScreenLoader } from '@trivie/ui-web/components/full-screen-loader'
import { HourlyWarning } from '@trivie/ui-web/components/hourly-warning'
import { NotificationCheckScreen } from './screens/splash/notification-check-screen'
import { Dialog } from '@trivie/ui-web/components/dialogs/dialog'
import { ConfirmProvider } from '@trivie/ui-web/components/dialogs/confirmation-dialog'

const queryString = require('query-string')

const packageJson = require('../package.json')

const { version } = packageJson

if (process.env.REACT_APP_ENV !== 'local' && process.env.REACT_APP_ENV !== 'dev') {
  Sentry.init({
    dsn: process.env.REACT_APP_SENTRY_DSN,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.reactRouterV5BrowserTracingIntegration({ history: BrowserHistory }),
    ],
    environment: process.env.REACT_APP_ENV,
    release: version,
    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for performance monitoring.
    // We recommend adjusting this value in production
    tracesSampleRate: 0,
  })
}

Sentry.setTag('variant', process.env.REACT_APP_VARIANT)

export const App: React.FunctionComponent = () => {
  const [rootStore, setRootStore] = useState<RootStoreType | undefined>(undefined)

  const showAlert = (status: string, message: string, reset?: boolean, back?: boolean) => {
    setAlertStatus(status)
    setAlertMessage(message)
    setForceLogout(Boolean(reset))
    setBack(Boolean(back))
    if (!alert) {
      setAlert(true)
    }
  }

  // Setup root store
  useEffect(() => {
    ;(async () => {
      console.log(`Trivie v${version}`)
      const config = {
        persist: true,
        storageKey: 'trivie',
        version,
        dev_mode: true,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      }
      setRootStore(
        await setupRootStore(config, (show: boolean) => setShowMaintenance(show), showAlert),
      )
    })()
  }, [])

  const [alert, setAlert] = useState(false)
  const [alertStatus, setAlertStatus] = useState('')
  const [back, setBack] = useState(false)
  const [alertMessage, setAlertMessage] = useState('')
  const [forceLogout, setForceLogout] = useState(false)
  const [showMaintenance, setShowMaintenance] = useState(false)

  const handleAlert = () => {
    setAlert(false)
    setBack(false)
  }

  useStatusCodeMiddleware({ rootStore, showAlert })

  const [tok, setTok] = useState(rootStore?.userStore.token)

  const theme = useMemo(
    () =>
      responsiveFontSizes(
        createTheme({
          ...baseTheme,
          typography: {
            ...baseTheme.typography,
            fontFamily: rootStore?.uiStore?.branding!.font_family || 'Inter',
          },
        }),
      ),
    [rootStore?.uiStore?.branding],
  )

  useEffect(() => {
    if (!rootStore?.userStore?.user && rootStore?.userStore.token) {
      if (localStorage.getItem('user_id')) {
        rootStore?.userStore.getUser(Number(localStorage.getItem('user_id')))
      }
    }
  }, [rootStore?.userStore?.user])

  // Wait for rootStore to initilaize
  if (!rootStore) {
    return null
  } else {
    load('show_tutorial').then((data) => {
      rootStore.quizStore.setShowTutorial(data == null ? true : data)
    })
  }

  return (
    <RootStoreProvider value={rootStore}>
      <I18nextProvider i18n={i1}>
        <Router history={BrowserHistory}>
          <SSOLinking setToken={setTok} />
          <BrandedSplashScreen />
          <ScrollToTop>
            <StyledEngineProvider injectFirst>
              <ThemeProvider theme={theme}>
                <ConfirmProvider defaultOptions={{}}>
                  <CssBaseline enableColorScheme />
                  <FontHandler />
                  <div style={ROOT}>
                    <Switch>
                      <Route path="/sso-error" render={() => <SSOError />} />
                      <Route path="/token/:token/(email)?/:email?" render={() => <MagicLink />} />
                      {loginRoutes.map((route: RouteType, index) => {
                        return (
                          <Route
                            key={index}
                            path={route.path}
                            exact={route.exact}
                            children={<route.main />}
                          />
                        )
                      })}
                      <Route
                        exact
                        path="/login/workspace/:access_code"
                        render={() => <TestLogin />}
                      />
                      <Route exact path="/login/sso/:accessCode">
                        <SSOEntryScreen />
                      </Route>
                      <Route exact path="/login/sms">
                        <SMSScreen />
                      </Route>
                      <Route exact path="/share/:code">
                        <ShareQuizScreen />
                      </Route>
                      <Route exact path="/logout">
                        <Logout />
                      </Route>
                      <ProtectedRoutes hasToken={tok} />
                    </Switch>
                  </div>
                  <MiddlewareDialog
                    title={alertStatus}
                    message={alertMessage}
                    isOpen={alert}
                    onClose={handleAlert}
                    back={back}
                    setBack={back}
                    forceLogout={forceLogout}
                  />
                  {showMaintenance && (
                    <MaintenanceDialog
                      onClose={() => setShowMaintenance(false)}
                      visible={showMaintenance}
                    />
                  )}
                  <NotificationCheckScreen />
                  <LanguageHandler />
                </ConfirmProvider>
              </ThemeProvider>
            </StyledEngineProvider>
          </ScrollToTop>
        </Router>
      </I18nextProvider>
    </RootStoreProvider>
  )
}

const FontHandler = observer(() => {
  const { uiStore } = useStores()

  const { branding } = uiStore

  const getFontStyleElement = () => {
    const element = document.getElementById('font')
    if (element && element instanceof HTMLStyleElement) {
      return element
    }
    const styleElement = document.createElement('style')
    styleElement.id = 'font'
    return styleElement
  }

  useEffect(() => {
    const fontFace = `
    @supports (font-variation-settings: normal) {
      @font-face {
        font-family: ${branding!.font_family};
        src: url(${branding!.web_font_url});
        src: url(${branding.web_font_url}) format('woff2');
      }
    }`
    const styleElement = getFontStyleElement()
    styleElement.innerHTML = ''
    styleElement.appendChild(document.createTextNode(fontFace))
    document.head.appendChild(styleElement)
  }, [branding?.font_family])
  return null
})

const SSOError = observer(() => {
  useEffect(() => {
    window.location.href = `trivielearn://sso-error`
  }, [])
  const history = useHistory()
  return (
    <>
      <Dialog
        open
        title="Login Unsuccessful"
        onConfirm={() => history.replace('/login')}
        confirmBtnLabel="Okay"
        hideCloseIcon
        maxWidth="sm"
        dialogText="There was an issue authenticating with your SSO provider. Please try again, or contact your learning administrator for assistance."
      />
    </>
  )
})

const MagicLink = observer(() => {
  const { userStore } = useStores()
  const { params } = useRouteMatch()

  const searchParams = queryString.parse(location.search)
  const { workspace_id } = searchParams

  const history = useHistory()

  const [showHourly, setShowHourly] = useState<string>('')
  const [evaluateData, setEvaluateData] = useState<any>()

  useEffect(() => {
    handleMagicLink()
  }, [])

  const loginSingleWorkspace = async (data, ws?) => {
    const deeplink = localStorage.getItem('deeplink')

    if (ws) {
      userStore.loginWorkspace(data.token, ws, () =>
        history.replace(
          userStore.sharedQuiz ? `/share/${userStore.sharedQuiz?.code}` : deeplink ?? '/library',
        ),
      )
    } else if (data.workspaces[0].has_sso_config) {
      userStore.setSSOWorkspace(data.workspaces[0])
      const ssoRedirect = await userStore?.getAccessCodeDetails(data.workspaces[0].access_code)
      if (ssoRedirect) {
        window.location.replace(ssoRedirect)
      }
    } else {
      userStore.loginWorkspace(data.token, data.workspaces[0].access_code, () =>
        history.replace(
          userStore.sharedQuiz ? `/share/${userStore.sharedQuiz?.code}` : deeplink ?? '/library',
        ),
      )
    }
  }

  const handleMagicLink = async () => {
    // @ts-ignore
    const { token, email } = params

    let tokenData
    try {
      tokenData = JSON.parse(atob(atob(token).split('.')[1]))
    } catch {
      tokenData = false
    }

    localStorage.removeItem('sso-linking')

    const deeplink = localStorage.getItem('deeplink')
    localStorage.removeItem('deeplink')

    if (!tokenData && workspace_id) {
      userStore.loginWorkspace(btoa(token), workspace_id, () =>
        history.replace(deeplink ?? `/library`),
      )
    } else if (token) {
      if (isMobile) {
        let flavor = location.host.split('.')[0]
        if (flavor === 'engage') {
          flavor = 'learn'
        } else if (flavor === 'boost') {
          flavor = 'pmb'
        }
        const path = window.location.pathname.split('/')
        const pathToken = path[2]
        const { source } = searchParams
        const ua = window.navigator.userAgent
        const iOS = !!ua.match(/iPad/i) || !!ua.match(/iPhone/i)
        const webkit = !!ua.match(/WebKit/i)
        const iOSSafari =
          iOS && webkit && !ua.match(/CriOS/i) && !!ua.match(/Version\/[\d\.]+.*Safari/)
        // if not mobile safari and have a token/email attempt to open app and login
        if ((pathToken && !iOSSafari) || (pathToken && iOSSafari && source !== 'welcome')) {
          window.location.href = `trivie${flavor}://${
            process.env.REACT_APP_ENV
          }/token/${pathToken}/${workspace_id ? `?workspace_id=${workspace_id}` : ''}`
        }
      }
      userStore.logout()
      userStore.evaluateMagicLink(token, email).then(async (data) => {
        if (userStore.status === 'error') {
          history.replace('/login/expired')
        } else if (data.workspaces && data.workspaces.length > 1) {
          localStorage.removeItem('ml-error')
          if (workspace_id) {
            userStore.loginWorkspace(data.token, workspace_id, () =>
              history.replace(
                userStore.sharedQuiz
                  ? `/share/${userStore.sharedQuiz?.code}`
                  : deeplink ?? '/library',
              ),
            )
          } else {
            if (userStore.sharedQuiz) {
              const ws = data.workspaces.find(
                (w) => w.access_code === userStore.sharedQuiz?.workspace_id,
              )
              if (ws) {
                userStore.loginWorkspace(data.token, userStore.sharedQuiz?.workspace_id, () =>
                  history.replace(`/share/${userStore.sharedQuiz?.code}`),
                )
              }
            } else {
              history.replace('/workspaces')
            }
          }
        } else if (data.workspaces.length === 1) {
          localStorage.removeItem('ml-error')
          // check if showing terms
          setEvaluateData(data)
          if (
            (data.workspaces[0].hourly_warning && !workspace_id) ||
            (data.workspaces[0].hourly_warning && localStorage.getItem('redirecting-sso'))
          ) {
            localStorage.removeItem('redirecting-sso')
            setShowHourly(data.workspaces[0].hourly_warning)
          } else {
            loginSingleWorkspace(data, workspace_id)
          }
        }
      })
    }
  }

  return showHourly ? (
    <HourlyWarning
      message={showHourly}
      accept={() => {
        if (evaluateData) {
          userStore
            .handleTerms('accept', evaluateData.token, evaluateData.workspaces[0].access_code)
            .then(() => {
              loginSingleWorkspace(evaluateData, evaluateData.workspaces[0].access_code)
            })
        }
        setShowHourly('')
      }}
      reject={() => {
        setShowHourly('')
        userStore
          .handleTerms('reject', evaluateData.token, evaluateData.workspaces[0].access_code)
          .then(() => {
            history.replace('/login')
          })
      }}
    />
  ) : (
    <FullScreenLoader status="Logging you in..." />
  )
})

const SSOLinking = observer((props: any) => {
  const { setToken } = props
  const query = useQuery()
  const { userStore } = useStores()
  const { logout, loginWorkspace, getAccessCodeDetails } = userStore
  const history = useHistory()

  const token = query.get('access_token')
  const ssoWorkspaceID = query.get('sso_workspace_id')

  const path = location.pathname

  const isSSO = async (access_code: string) => {
    const workspace = await userStore.workspaceVerify(access_code)
    if (workspace?.has_sso_config) {
      return true
    }
    return false
  }

  if (ssoWorkspaceID) {
    localStorage.setItem('deeplink', path)
    localStorage.setItem('sso-linking', 'true')
    getAccessCodeDetails(ssoWorkspaceID).then((redirect) => {
      if (redirect) {
        window.location.replace(redirect)
      }
    })
  } else if (token) {
    const tokenData = JSON.parse(decode(decode(token).split('.')[1]))
    const { access_code } = tokenData
    logout()
    // verify sso workspace using access code from token
    isSSO(access_code).then((sso: boolean) => {
      if (sso) {
        localStorage.setItem('deeplink', path)
        // if sso workspace, get redirect url
        getAccessCodeDetails(access_code).then((redirect) => {
          if (redirect) {
            window.location.replace(redirect)
          }
        })
      } else {
        // not sso, login as usual
        loginWorkspace(token, access_code).then((authToken: any) => {
          setToken(authToken)
          query.delete('access_token')
          setTimeout(() => {
            history.replace(`${path}?${query.toString()}`)
          }, 500)
        })
      }
    })
  }

  return null
})

const LanguageHandler = observer((props: any) => {
  const { userStore } = useStores()

  const { i18n } = useTranslation()
  useEffect(() => {
    if (userStore.user) {
      i18n.changeLanguage(userStore.user.language ?? 'en')
    }
  }, [userStore.user])
  return null
})

function useQuery() {
  const { search } = useLocation()

  return React.useMemo(() => new URLSearchParams(search), [search])
}

const ROOT = { display: 'flex', height: '100%', overflow: 'hidden', backgroundColor: '#fff' }
